import { useEffect, useState } from 'react';
import { makeStyles, Button, TextField, Select, MenuItem, Typography } from '@material-ui/core';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';

export type Namespace = {
  name: string;
  region: string;
  clusterCode: string;
  baseDomain: string;
};

export type Region = {
  region: string;
  clusterCode: string;
  baseDomain: string;
};

const useStyles = makeStyles({
  namespaceFlexContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: '20px',
  },
  namespaceItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  },
  namespaceNameInput: {
    marginRight: '10px',
    flex: 1,
  },
  locationSelect: {
    marginRight: '10px',
  },
  namespaceLabel: {
    marginRight: '10px',
    padding: '13px',
    border: '2px solid #002743',
    borderRadius: '4px',
  },
  addButton: {
    marginTop: '20px',
  },
  heading: {
    marginBottom: '20px',
  },
});

export const ProductionNamespacesField = (
  props: FieldExtensionComponentProps<Namespace[], { applicationNameField: string }>
) => {
  const classes = useStyles();
  const { onChange, formContext, formData } = props;

  const [appName, setAppName] = useState(formContext?.formData.application_name ?? "application_name");
  const [namespaces, setNamespaces] = useState<Namespace[]>(formData || []);
  const regionData = (props.schema.items as any).options;

  useEffect(() => {
    setAppName(formContext?.formData.application_name);
  }, [formContext?.formData]);

  function onChangeHelper(updatedNamespaces: Namespace[]) {
    setNamespaces(updatedNamespaces);
    onChange(updatedNamespaces);
  }

  const addNamespace = () => {
    const defaultRegion = regionData.find((region: Region) => region.region === 'us-east-1') || regionData[0];
    const ns = { ...defaultRegion, name: 'production' };
    const updatedNamespaces = [...namespaces, ns]
    onChangeHelper(updatedNamespaces);
  };

  const updateNamespace = (index: number, field: 'name' | 'region', value: string) => {
    const updatedNamespaces = [...namespaces];

    if (field === 'name') {
      updatedNamespaces[index] = { ...updatedNamespaces[index], name: value };
    } else if (field === 'region') {
      const selectedRegion = regionData.find((region: Region) => region.region === value);
      if (selectedRegion) {
        updatedNamespaces[index] = { ...selectedRegion, name: updatedNamespaces[index].name };
      }
    }

    onChangeHelper(updatedNamespaces);
  };

  const removeNamespace = (index: number) => {
    const updatedNamespaces = namespaces.filter((_, i) => i !== index);
    onChangeHelper(updatedNamespaces);
  };

  return (
    <div className={classes.namespaceFlexContainer}>
      <Typography variant="body1" className={classes.heading}>
        Production Environments
      </Typography>

      {namespaces.map((namespace, index) => {
        if (!namespace.region) {
          return <></>
        } else {
          return (
            <div key={`${index}-key`} className={classes.namespaceItem}>
              <Typography className={classes.namespaceLabel}>
                {`${appName || 'defaultAppName'}-${namespace.name || 'production'}${namespace.region !== 'us-east-1' ? '-' + namespace.region : ''}`}
              </Typography>

              <TextField
                className={classes.namespaceNameInput}
                label={`Environment ${index + 1}`}
                variant="outlined"
                value={namespace.name}
                onChange={(e) => updateNamespace(index, 'name', e.target.value)}
              />
              <Select
                className={classes.locationSelect}
                value={namespace.region}
                onChange={(e) => updateNamespace(index, 'region', (e.target as HTMLInputElement).value)}
              >
                {regionData.map((region: Region) => (
                  <MenuItem key={region.region} value={region.region}>
                    {region.region.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => removeNamespace(index)}
              >
                Remove
              </Button>
            </div>
          )
        }
      }
      )}
      <Button
        className={classes.addButton}
        variant="contained"
        color="primary"
        onClick={addNamespace}
      >
        + Add Environment
      </Button>
    </div>
  );
};

export const productionNamespacesValidation = (
  value: Namespace[],
  validation: FieldValidation
) => {
  value.forEach((namespace, index) => {
    const suffix = `-${namespace.name}${namespace.region !== "us-east-1" ? '-' + namespace.region : ''}`;

    if (!namespace.name) {
      validation.addError(
        `Namespace name required.`
      );
    }

    if ((suffix.length + 25) > 55) {
      validation.addError(
        `Namespace ${index + 1} name must be less than 55 characters; '${suffix}' has: ${suffix.length}, which in combination of application name exceeds the limit`
      );
    }
  });
};
